import { gql } from '@apollo/client';
import { loader } from 'graphql.macro';

const MODEL_FIELDS_FRAGMENT = gql`
  fragment modelFields on model {
    id
    file_name
    owner_id
    model_id
    model_version
    sha
    created_at
    updated_at
    model_tags {
      id
      tag {
        id
        tag
      }
    }
  }
`;

const PIPELINE_FIELDS_FRAGMENT = gql`
    fragment pipelineFields on pipeline {
        id
        pipeline_id
        owner_id
        created_at
        updated_at
        visibility
        pipeline_tags {
            id
            pipeline_pk_id
            tag_pk_id
            tag {
                id
                tag
            }
        }
        pipeline_versions {
            id
            version
            created_at
            updated_at
            deployment_pipeline_versions {
                deployment {
                    deployed
                    engine_config
                }
            }
        }
    }
`;

const PIPELINE_VERSION_FIELDS_FRAGMENT = gql`
    fragment pipelineVersionFields on pipeline_version {
        id
        version
        definition
        created_at
        updated_at
        pipeline {
            id
            pipeline_id
            owner_id
            created_at
            updated_at
            visibility
            pipeline_tags {
                id
                pipeline_pk_id
                tag_pk_id
                tag {
                    id
                    tag
                }
            }
        }
        deployment_pipeline_versions {
            id
            pipeline_version_pk_id
            created_at
            updated_at
            deployment {
                id
                deploy_id
                created_at
                updated_at
                deployed
                engine_config
                deployment_model_configs {
                  model_config_id
                  model_config {
                    model {
                      sha
                    }
                  }
                }
            }
        }
    }
`;

export const FETCH_MODEL_QUERY = gql`
  ${MODEL_FIELDS_FRAGMENT}

  query GetModel($model_id: String, $model_version: String) {
    model(where: { model_id: { _eq: $model_id }, model_version: { _eq: $model_version } }) {
      ...modelFields
    }
  }
`;

export const FETCH_MODEL_VERSIONS_QUERY = gql`
    ${MODEL_FIELDS_FRAGMENT}

    query GetModelVersions($model_id: String, $workspace_id: bigint) {
      model(
        where: { 
          model_id: { _eq: $model_id }
          model: {
            workspace_id: {_eq: $workspace_id}
          }
        }) {
          ...modelFields
      }
    }
`;

export const FETCH_PIPELINE_QUERY = gql`
    ${PIPELINE_FIELDS_FRAGMENT}

    query GetPipeline($pipeline_id: String) {
        pipeline(where: { pipeline_id: { _eq: $pipeline_id } }) {
            ...pipelineFields
        }
    }
`;

export const FETCH_PIPELINE_VERSION_QUERY = gql`
    ${PIPELINE_VERSION_FIELDS_FRAGMENT}

    query FetchPipelineVersion($pipeline_id: String!, $workspace_id: bigint) {
        pipeline_version(
            where: {
                pipeline: {
                    pipeline_id: { _eq: $pipeline_id }
                    workspace_id: {_eq: $workspace_id}
                }
            }
            order_by: { created_at: desc_nulls_last }
        ) {
            ...pipelineVersionFields,
        }
    }
`;

export const SEARCH_MODELS_QUERY = gql`
  ${MODEL_FIELDS_FRAGMENT}

  query SearchModels($query: String, $workspace_id: bigint, $pipeline_order: order_by) {
      search_workspace_models(
          args: {search: $query, workspace_id: $workspace_id},
          order_by: { updated_at: desc_nulls_last }
      ) {
          ...modelFields
      }
  }
`;

export const SEARCH_PIPELINES_QUERY = gql`
    ${PIPELINE_VERSION_FIELDS_FRAGMENT}

    query SearchPipelines($query: String $workspace_id: bigint, $pipeline_order: order_by) {
        search_workspace_pipelines(
            args: { search: $query, workspace_id: $workspace_id },
            order_by: { updated_at: desc_nulls_last }
        ) {
            ...pipelineVersionFields
        }
    }
`;

export const ADD_MODEL_TAG_QUERY = gql`
  mutation AddModelTag($model_id: bigint, $tag_name: String) {
    insert_model_tag_one(object: { model_id: $model_id, tag: { data: { tag: $tag_name } } }) {
      model_id
      tag_id
      tag {
        tag
      }
    }
  }
`;

export const ADD_PIPELINE_TAG_QUERY = gql`
    mutation AddPipelineTag($pipeline_id: bigint, $tag_name: String) {
        insert_pipeline_tag_one(object: { pipeline_pk_id: $pipeline_id, tag: { data: { tag: $tag_name } } }) {
            pipeline_pk_id
            tag_pk_id
            tag {
                id
                tag
            }
        }
    }
`;

export const DELETE_MODEL_TAG_QUERY = gql`
  mutation DeleteModelTag($model_tag_id: bigint) {
    delete_model_tag(where: { id: { _eq: $model_tag_id } }) {
      returning {
        id
      }
    }
  }
`;

export const DELETE_PIPELINE_TAG_QUERY = gql`
    mutation DeletePipelineTag($pipeline_tag_id: bigint) {
        delete_pipeline_tag(where: { id: { _eq: $pipeline_tag_id } }) {
            returning {
                id
            }
        }
    }
`;


export const FETCH_MODEL_COMMENTS_QUERY = gql`
  query FetchModelComments(
    $model_pk_id: bigint!
    $limit: Int!
    $idFilter: [model_variant_comment_bool_exp]
  ) {
    model_variant_comment(
      where: { model_pk_id: { _eq: $model_pk_id }, _and: $idFilter }
      order_by: [{ id: desc }]
      limit: $limit
    ) {
      id
      model_pk_id
      comment {
        id
        message
        user_id
        created_at
        updated_at
        deleted_at
      }
      model {
        model {
          workspace_id
        }
      }
    }
  }
`;

export const FETCH_PIPELINES_COMMENTS_QUERY = gql`
    query FetchPipelineComments(
        $pipeline_pk_id: bigint!
        $limit: Int!
        $idFilter: [pipeline_comment_bool_exp]
    ) {
        pipeline_comment(
            where: { pipeline_pk_id: { _eq: $pipeline_pk_id }, _and: $idFilter }
            order_by: [{ id: desc }]
            limit: $limit
        ) {
            id
            pipeline_pk_id
            comment {
                id
                message
                user_id
                created_at
                updated_at
                deleted_at
            }
            pipeline {
              workspace_id
            }
        }
    }
`;

export const ADD_MODEL_COMMENT_QUERY = gql`
  mutation AddComment($model_pk_id: bigint!, $message: String!, $user_id: String!) {
    insert_model_variant_comment_one(
      object: {
        model_pk_id: $model_pk_id
        comment: { data: { message: $message, user_id: $user_id } }
      }
    ) {
      id
      model_pk_id
      comment_id
      comment {
        id
        message
        user_id
      }
      model {
        model {
          workspace_id
        }
      }
    }
  }
`;

export const ADD_PIPELINE_COMMENT_QUERY = gql`
    mutation AddComment($pipeline_pk_id: bigint!, $message: String!, $user_id: String!) {
        insert_pipeline_comment_one(
            object: {
                pipeline_pk_id: $pipeline_pk_id
                comment: { data: { message: $message, user_id: $user_id } }
            }
        ) {
            id
            pipeline_pk_id
            comment_pk_id
            comment {
                id
                message
                user_id
            }
            pipeline {
              workspace_id
            }        
        }
    }
`;

export const UPDATE_MODEL_COMMENT_QUERY = gql`
  mutation UpdateComment($commentID: bigint!, $updatedMessage: String!) {
    update_comment_by_pk(pk_columns: { id: $commentID }, _set: { message: $updatedMessage }) {
      id
      message
      updated_at
    }
  }
`;

export const UPDATE_PIPELINE_COMMENT_QUERY = gql`
    mutation UpdateComment($commentID: bigint!, $updatedMessage: String!) {
        update_comment_by_pk(pk_columns: { id: $commentID }, _set: { message: $updatedMessage }) {
            id
            message
            updated_at
        }
    }
`;

export const DELETE_MODEL_COMMENT_QUERY = gql`
  mutation DeleteComment($commentID: bigint!, $deletedAt: timestamptz!) {
    update_comment_by_pk(
      pk_columns: { id: $commentID }
      _set: { message: "", deleted_at: $deletedAt }
    ) {
      id
      message
      deleted_at
      updated_at
    }
  }
`;

export const DELETE_PIPELINE_COMMENT_QUERY = gql`
    mutation DeleteComment($commentID: bigint!, $deletedAt: timestamptz!) {
        update_comment_by_pk(
            pk_columns: { id: $commentID }
            _set: { message: "", deleted_at: $deletedAt }
        ) {
            id
            message
            deleted_at
            updated_at
        }
    }
`;

export const FETCH_ALL_MODEL_COMMENTS_QUERY = gql`
  query FetchAllModelComments($workspaceId: bigint!, $limit: Int!, $idFilter: [model_variant_comment_bool_exp!]) {
    model_variant_comment(where: { model: { model: { workspace_id: {_eq: $workspaceId} } }, _and: $idFilter }, order_by: [{ id: desc }], limit: $limit) {
      id
      model_pk_id
      comment {
        id
        message
        user_id
        created_at
        updated_at
        deleted_at
      }
      model {
        model {
          workspace_id
        }
      }
    }
  }
`;

export const FETCH_ALL_PIPELINE_COMMENTS_QUERY = gql`
    query FetchAllPipelineComments($workspaceId: bigint!, $limit: Int!, $idFilter: [pipeline_comment_bool_exp!]) {
        pipeline_comment(where: { pipeline: { workspace_id: {_eq: $workspaceId} }, _and: $idFilter }, order_by: [{ id: desc }], limit: $limit) {
            id
            pipeline_pk_id
            comment {
                id
                message
                user_id
                created_at
                updated_at
                deleted_at
            }
            pipeline {
              workspace_id
            }
        }
    }
`;

export const FETCH_ALL_PIPELINE_VERSION_DEFINITIONS_QUERY = gql`
  query FetchAllPipelineVersionDefinitions {
    pipeline_version {
      id
      definition
    }
  }
`;

export const FETCH_MODEL_ASSOC_PIPELINES = loader('../../api/graphql/GetPipelinesForModels.graphql');
export const FETCH_PIPELINES_ASSOC_MODELS = loader('../../api/graphql/pipelines/PipelineModelsFlat.graphql');
export const GET_PIPELINE_BY_NAME_QUERY = loader('../../api/graphql/GetPipelineByName.graphql');

export const transformModelResponse = ({
  file_name,
  id,
  model_id,
  owner_id,
  model_tags,
  model_version,
  created_at,
  updated_at,
  sha,
}) => ({
  id,
  sha,
  fileName: file_name,
  modelID: model_id,
  ownerID: owner_id,
  modelTags: model_tags,
  modelVersion: model_version,
  createdAt: created_at,
  updatedAt: updated_at,
});

export const transformDeploymentPipelineVersionResponse = ({
  id,
  pipeline_version_pk_id,
  created_at,
  updated_at,
  deployment,
}) => ({
  id,
  deploymentPrimaryKey: pipeline_version_pk_id,
  createdAt: created_at,
  updatedAt: updated_at,
  deployed: deployment?.deployed,
  deploymentID: deployment?.deploy_id,
  deployedAt: deployment?.createdAt,
  deploymentUpdatedAt: deployment?.updatedAt,
  engineConfig: deployment?.engine_config,
  modelConfigs: deployment?.deployment_model_configs
})

export const transformPipelineVersionResponse = ({
  id,
  definition,
  version,
  created_at,
  updated_at,
  deployment_pipeline_versions,
}) => ({
  id,
  version,
  updatedAt: updated_at,
  createdAt: created_at,
  deployment_pipeline_versions,
  deployments: deployment_pipeline_versions?.map(transformDeploymentPipelineVersionResponse),
  steps: definition?.steps,
});

const isPipelineVersionResponseActive = ({
  deployment_pipeline_versions,
}) => deployment_pipeline_versions?.some(deploymentVersion => deploymentVersion?.deployment?.deployed) || false

const isPipelineResponseActive = pipeline_versions => pipeline_versions?.some(pipelineVersion => isPipelineVersionResponseActive(pipelineVersion)) || false;

export const transformPipelineResponse = ({
  id,
  pipeline_id,
  owner_id,
  created_at,
  updated_at,
  visibility,
  pipeline_versions,
  pipeline_tags,
  deployment
}) => ({
  id,
  visibility,
  pipelineID: pipeline_id,
  ownerID: owner_id,
  createdAt: created_at,
  updatedAt: updated_at,
  pipelineTags: pipeline_tags,
  pipelineVersions: pipeline_versions?.map(transformPipelineVersionResponse) || [],
  deployment,
  status: isPipelineResponseActive(pipeline_versions) ? 'Active' : 'Inactive',
});

export const transformPipelineSearchResponse = (pipelineVersion) => {
  const {
    pipeline: {
      id,
      pipeline_id,
      owner_id,
      created_at,
      updated_at,
      visibility,
      pipeline_tags,
    },
  } = pipelineVersion;
  return ({
    id,
    visibility,
    pipelineID: pipeline_id,
    ownerID: owner_id,
    createdAt: created_at,
    updatedAt: updated_at,
    pipelineTags: pipeline_tags,
    latestVersion: transformPipelineVersionResponse(pipelineVersion),
    status: isPipelineVersionResponseActive(pipelineVersion) ? 'Active' : 'Inactive',
  });
}
